import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Fade,
  useTheme,
  alpha,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  Add as AddIcon,
  MoreVert as MoreIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  CheckCircle as ConnectedIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
// REMOVED: import { motion } from "framer-motion"; - unused and adds 100KB to bundle
import PortfolioConnectionWizard from "./Wizard/PortfolioConnectionWizard";
import {
  useLinkedAccounts,
  useDeleteAccount,
} from "../../hooks/useLinkedAccounts";
import { getBrokerById, BrokerProvider } from "../../data/brokers";

const PortfolioManagePage = () => {
  const theme = useTheme();
  const [showWizard, setShowWizard] = useState(false);
  const [selectedBroker, setSelectedBroker] = useState<BrokerProvider | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);

  const { data: accountsData, isLoading } = useLinkedAccounts();
  const deleteAccountMutation = useDeleteAccount();
  const accounts = accountsData?.accounts || [];

  // Auto-open wizard if no accounts exist and not loading
  useEffect(() => {
    if (!isLoading && accounts.length === 0) {
      setShowWizard(true);
    }
  }, [isLoading, accounts.length]);

  const handleAddAccount = (broker?: BrokerProvider) => {
    setSelectedBroker(broker || null);
    setShowWizard(true);
  };

  const handleWizardClose = () => {
    setShowWizard(false);
    setSelectedBroker(null);
  };

  const handleAccountMenu = (
    event: React.MouseEvent<HTMLElement>,
    account: any
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedAccount(account);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedAccount(null);
  };

  const handleDisconnectClick = () => {
    setDisconnectDialogOpen(true);
    setAnchorEl(null); // Just close the menu without clearing selectedAccount
  };

  const handleDisconnectConfirm = async () => {
    if (selectedAccount?.id) {
      try {
        await deleteAccountMutation.mutateAsync(selectedAccount.id);
        setDisconnectDialogOpen(false);
        setSelectedAccount(null);
      } catch (error) {
        // Error handling will be handled by the mutation hook
      }
    }
  };

  const handleDisconnectCancel = () => {
    setDisconnectDialogOpen(false);
    setSelectedAccount(null); // Clear selectedAccount when canceling
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
      },
    },
  };

  // If wizard is active, render it full-page
  if (showWizard) {
    return (
      <PortfolioConnectionWizard
        onClose={handleWizardClose}
        preselectedBroker={selectedBroker}
      />
    );
  }

  // Otherwise render normal portfolio page
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          opacity: 1,
          // Removed framer-motion for performance testing
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h3"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                // Temporarily disabled gradient text for performance testing
                // background:
                //   theme.palette.mode === "dark"
                //     ? "linear-gradient(135deg, #fff 0%, #a1a1a6 100%)"
                //     : "linear-gradient(135deg, #1d1d1f 0%, #86868b 100%)",
                // WebkitBackgroundClip: "text",
                // WebkitTextFillColor: "transparent",
              }}
            >
              Portfolio
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your connected investment accounts and track performance
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleAddAccount()}
            size="large"
            sx={{
              borderRadius: 3,
              px: 3,
              background:
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(22, 163, 74, 0.8) 100%)"
                  : "linear-gradient(135deg, rgba(22, 163, 74, 1) 0%, rgba(34, 197, 94, 0.9) 100%)",
              boxShadow: theme.customShadows.elevated[theme.palette.mode],
              // Simplified hover for performance
              "&:hover": {
                boxShadow: theme.shadows[2],
              },
            }}
          >
            Add Account
          </Button>
        </Box>
      </Box>

      {/* Connected Accounts */}
      {accounts.length > 0 ? (
        <Box>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
            Connected Accounts
          </Typography>
          <Stack
            direction="row"
            spacing={3}
            sx={{
              flexWrap: "wrap",
              "& > *": {
                flex: {
                  xs: "1 1 100%",
                  sm: "1 1 calc(50% - 12px)",
                  md: "1 1 calc(33.333% - 16px)",
                },
                minWidth: { xs: "100%", sm: "300px", md: "280px" },
                maxWidth: {
                  xs: "100%",
                  sm: "calc(50% - 12px)",
                  md: "calc(33.333% - 16px)",
                },
              },
            }}
          >
            {accounts.map((account: any) => {
              const broker = getBrokerById(account.provider);
              if (!broker) return null;

              return (
                <Box key={account.id}>
                  <Card
                    sx={{
                      height: "100%",
                      position: "relative",
                      overflow: "visible",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      // Simplified hover for performance
                      "&:hover": {
                        boxShadow: theme.shadows[4],
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 2 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              backgroundColor: alpha(
                                account.account_status === "active"
                                  ? theme.palette.success.main
                                  : theme.palette.warning.main,
                                0.1
                              ),
                              color:
                                account.account_status === "active"
                                  ? theme.palette.success.main
                                  : theme.palette.warning.main,
                            }}
                          >
                            <broker.iconComponent size={24} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {broker.displayName}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {account.account_nickname}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={(e) => handleAccountMenu(e, account)}
                        >
                          <MoreIcon />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Chip
                          icon={
                            account.account_status === "active" ? (
                              <ConnectedIcon />
                            ) : (
                              <WarningIcon />
                            )
                          }
                          label={
                            account.account_status === "active"
                              ? "Active"
                              : "Needs Attention"
                          }
                          size="small"
                          color={
                            account.account_status === "active"
                              ? "success"
                              : "warning"
                          }
                        />
                      </Box>

                      {account.last_sync_at && (
                        <Typography variant="body2" color="text.secondary">
                          Last sync:{" "}
                          {new Date(account.last_sync_at).toLocaleString()}
                        </Typography>
                      )}

                      {account.last_sync_status === "error" &&
                        account.last_sync_error && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 1, display: "block" }}
                          >
                            {account.last_sync_error}
                          </Typography>
                        )}
                    </CardContent>
                  </Card>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ) : (
        /* Empty State */
        <Fade in>
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.02)"
                  : "rgba(0, 0, 0, 0.01)",
              borderRadius: 3,
              border: `2px dashed ${
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)"
              }`,
            }}
          >
            <Typography
              variant="h5"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: "text.secondary",
              }}
            >
              No accounts connected yet
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Connect your investment accounts to start tracking your portfolio
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => handleAddAccount()}
              sx={{
                borderRadius: 3,
                px: 4,
              }}
            >
              Connect Your First Account
            </Button>
          </Box>
        </Fade>
      )}

      {/* Account Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <SyncIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Sync Now</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCloseMenu}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Manage</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDisconnectClick} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Disconnect</ListItemText>
        </MenuItem>
      </Menu>


      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={handleDisconnectCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 600 }}>
          Disconnect Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to disconnect your{" "}
            <strong>
              {selectedAccount &&
                getBrokerById(selectedAccount.provider)?.displayName}
            </strong>{" "}
            account?
          </DialogContentText>
          <DialogContentText variant="body2" color="warning.main">
            This will remove all portfolio data and transaction history
            associated with this account. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleDisconnectCancel}
            variant="text"
            sx={{ color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisconnectConfirm}
            variant="contained"
            color="error"
            disabled={deleteAccountMutation.isPending}
            sx={{ minWidth: 100 }}
          >
            {deleteAccountMutation.isPending
              ? "Disconnecting..."
              : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfolioManagePage;
